import React, { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { MessageCircleQuestion, Send, Loader2, User, Bot, RefreshCw } from "lucide-react";

const AUTH_NAME = {
  gaca: { en: "GACA", ar: "الطيران المدني" },
  cst: { en: "CST", ar: "الاتصالات" },
  tga: { en: "TGA", ar: "النقل" },
};

const PolicyChat = () => {
  const { language, category } = useApp();
  const [messages, setMessages] = useState([]); // [{role: 'user'|'assistant', content}]
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q || loading) return;
    const nextHistory = [...messages, { role: "user", content: q }];
    setMessages(nextHistory);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", {
        category,
        language,
        question: q,
        history: messages,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: data.answer }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t(language, "errorGeneric"));
      setMessages(nextHistory); // keep user question, drop failed assistant reply
    } finally {
      setLoading(false);
    }
  };

  const clear = () => setMessages([]);
  const authLabel = AUTH_NAME[category]?.[language] || "";

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col"
      data-testid="policy-chat"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="text-start">
          <div className="flex items-center gap-2">
            <MessageCircleQuestion className="h-5 w-5 text-[#006C35]" />
            <h2 className="text-lg font-semibold text-slate-900">{t(language, "askAboutPolicies")}</h2>
          </div>
          <p className="text-xs text-slate-500 mt-1">{t(language, "askHelper")}</p>
          <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[#C5A059] bg-[#C5A059]/10 px-2 py-0.5 rounded uppercase tracking-wide">
            {authLabel}
          </div>
        </div>
        {messages.length > 0 && (
          <Button
            data-testid="chat-clear-btn"
            type="button"
            size="sm"
            variant="ghost"
            onClick={clear}
            className="text-slate-500 hover:text-slate-800"
          >
            <RefreshCw className="h-3.5 w-3.5 me-1.5" />
            {t(language, "clearChat")}
          </Button>
        )}
      </div>

      <div
        ref={scrollRef}
        data-testid="chat-messages"
        className="flex-1 min-h-[220px] max-h-[420px] overflow-y-auto space-y-3 mb-4 pe-1"
      >
        {messages.length === 0 && !loading && (
          <div className="h-full flex items-center justify-center text-center text-sm text-slate-400 py-8">
            {t(language, "askEmptyState")}
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={`chat-msg-${i}`}
            className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
          >
            <div
              className={`h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                m.role === "user" ? "bg-slate-100 text-slate-600" : "bg-[#006C35] text-white"
              }`}
            >
              {m.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
            </div>
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap text-start ${
                m.role === "user"
                  ? "bg-slate-100 text-slate-800 rounded-tr-md"
                  : "bg-[#F0FDF4] text-slate-800 border border-green-100 rounded-tl-md"
              }`}
              dir={language === "ar" ? "rtl" : "ltr"}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-3" data-testid="chat-loading">
            <div className="h-8 w-8 rounded-full bg-[#006C35] text-white flex items-center justify-center flex-shrink-0">
              <Bot className="h-4 w-4" />
            </div>
            <div className="bg-[#F0FDF4] border border-green-100 rounded-2xl rounded-tl-md px-4 py-2.5 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t(language, "asking")}
            </div>
          </div>
        )}
      </div>

      <form onSubmit={send} className="flex gap-2 items-center">
        <Input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t(language, "askPlaceholder")}
          className="flex-1 border-slate-200 focus:border-[#006C35] focus:ring-[#006C35]/20"
          dir={language === "ar" ? "rtl" : "ltr"}
          disabled={loading}
        />
        <Button
          data-testid="chat-send-btn"
          type="submit"
          disabled={loading || !input.trim()}
          className="h-10 bg-[#006C35] hover:bg-[#004F27] text-white flex-shrink-0"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          <span className="ms-2 hidden sm:inline">{t(language, "askSend")}</span>
        </Button>
      </form>
      <p className="text-[11px] text-slate-400 mt-3 text-start">
        {t(language, "askDisclaimer")}
      </p>
    </div>
  );
};

export default PolicyChat;
